import {
  Check as CheckIcon,
  Close as CloseIcon,
  Sync as SyncIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import MarketGrowthAdminApi from "../../../../../infrastructure/api/marketGrowthAdmin";
import { getEnumLabel } from "../constants/commissionEnums";
import {
  PARTNER_LEVEL_LABELS,
  PARTNER_STATUS_LABELS,
  getPartnerStatusChipColor,
  isPartnerPendingActivation,
} from "../constants/partnerEnums";
import AdminActionConfirmDialog from "../components/AdminActionConfirmDialog";
import MarketGrowthDataTable, {
  TABLE_ACTION_ICON_SX,
} from "../components/MarketGrowthDataTable";
import MarketGrowthPageShell from "../components/MarketGrowthPageShell";

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString("fa-IR") : "—";

const formatPersonCell = (name, mobile) => (
  <>
    <Typography variant="body2" fontWeight={600}>
      {name || "—"}
    </Typography>
    {mobile ? (
      <Typography variant="caption" color="text.secondary" display="block">
        {mobile}
      </Typography>
    ) : null}
  </>
);

const ACTION_CONFIG = {
  approve: {
    title: "تأیید نماینده",
    description:
      "با تأیید، نماینده فعال می‌شود و پیامک خوش‌آمدگویی برای او ارسال می‌شود.",
    confirmLabel: "تأیید",
    confirmColor: "success",
    showNotes: false,
    targetStatus: "Active",
  },
  reject: {
    title: "رد درخواست نمایندگی",
    description: "آیا این درخواست رد شود؟ نماینده غیرفعال خواهد شد.",
    confirmLabel: "رد",
    confirmColor: "error",
    showNotes: false,
    targetStatus: "Inactive",
  },
};

const MarketGrowthPartnerApprovalsPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [allPartners, setAllPartners] = useState([]);
  const [actionState, setActionState] = useState({ open: false, row: null, action: null });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [pendingRes, partnersRes] = await Promise.all([
        MarketGrowthAdminApi.getPendingApprovalPartners(),
        MarketGrowthAdminApi.getPartners(),
      ]);
      const pendingNorm = MarketGrowthAdminApi.normalizeList(
        pendingRes,
        "بارگذاری نمایندگان در انتظار تایید ناموفق بود."
      );
      const partnersNorm = MarketGrowthAdminApi.normalizeList(
        partnersRes,
        "بارگذاری نمایندگان ناموفق بود."
      );
      if (pendingNorm.error) {
        setError(pendingNorm.error);
        setItems([]);
        return;
      }
      setItems(Array.isArray(pendingNorm.data) ? pendingNorm.data : []);
      setAllPartners(Array.isArray(partnersNorm.data) ? partnersNorm.data : []);
    } catch (e) {
      setError(e?.message || "خطا در بارگذاری");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const parentMap = useMemo(
    () =>
      allPartners.reduce((acc, p) => {
        acc[p.id] =
          p.displayName || p.mobileNumber || p.referralCode || `#${p.id}`;
        return acc;
      }, {}),
    [allPartners]
  );

  const tableRows = useMemo(
    () =>
      items.map((row) => ({
        ...row,
        personLabel: row.displayName || row.mobileNumber || String(row.userId),
        personSearch: [row.displayName, row.mobileNumber, row.referralCode]
          .filter(Boolean)
          .join(" "),
        levelLabel: getEnumLabel(PARTNER_LEVEL_LABELS, row.partnerLevel),
        statusLabel: getEnumLabel(PARTNER_STATUS_LABELS, row.status),
        parentLabel: row.parentPartnerId
          ? parentMap[row.parentPartnerId] || `#${row.parentPartnerId}`
          : "—",
        joinedAtLabel: formatDate(row.joinedAt),
        joinedAt: row.joinedAt,
      })),
    [items, parentMap]
  );

  const headCells = useMemo(
    () => [
      {
        id: "personSearch",
        label: "نماینده",
        render: (_, row) => formatPersonCell(row.personLabel, row.mobileNumber),
      },
      { id: "levelLabel", label: "سطح" },
      {
        id: "statusLabel",
        label: "وضعیت",
        render: (_, row) => (
          <Chip
            size="small"
            label={getEnumLabel(PARTNER_STATUS_LABELS, row.status)}
            color={getPartnerStatusChipColor(row.status)}
          />
        ),
      },
      { id: "parentLabel", label: "مدیر فروش" },
      { id: "referralCode", label: "کد معرف" },
      { id: "joinedAt", label: "تاریخ ثبت‌نام", type: "date" },
    ],
    []
  );

  const tableActions = useMemo(
    () => [
      {
        icon: <CheckIcon sx={TABLE_ACTION_ICON_SX} />,
        onClick: (row) => setActionState({ open: true, row, action: "approve" }),
        color: "success",
        tooltip: "تأیید",
        hidden: (row) => !isPartnerPendingActivation(row.status),
      },
      {
        icon: <CloseIcon sx={TABLE_ACTION_ICON_SX} />,
        onClick: (row) => setActionState({ open: true, row, action: "reject" }),
        color: "error",
        tooltip: "رد",
        hidden: (row) => !isPartnerPendingActivation(row.status),
      },
    ],
    []
  );

  const handleActionConfirm = async () => {
    const { row, action } = actionState;
    const config = ACTION_CONFIG[action];
    if (!row || !config) return;

    setSaving(true);
    try {
      const res = await MarketGrowthAdminApi.patchPartnerStatus(row.id, config.targetStatus);
      const normalized = MarketGrowthAdminApi.normalize(res, "عملیات ناموفق بود.");
      if (normalized.error) {
        toast.error(normalized.error);
        return;
      }
      toast.success(
        action === "approve"
          ? "نماینده تأیید شد. پیامک اطلاع‌رسانی ارسال می‌شود."
          : "درخواست نمایندگی رد شد."
      );
      setActionState({ open: false, row: null, action: null });
      await load();
    } catch (e) {
      toast.error(e?.message || "خطا در انجام عملیات");
    } finally {
      setSaving(false);
    }
  };

  const actionConfig = actionState.action ? ACTION_CONFIG[actionState.action] : null;

  return (
    <MarketGrowthPageShell
      title="نمایندگان در انتظار تأیید"
      description="لیست یکپارچه درخواست‌های نمایندگی که هنوز تأیید نشده‌اند."
      loading={loading}
      error={error}
      onRetry={load}
    >
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Tooltip title="بارگذاری مجدد">
          <IconButton onClick={load}>
            <SyncIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {!loading && !error && items.length === 0 ? (
        <Alert severity="success">در حال حاضر نماینده‌ای در انتظار تأیید نیست.</Alert>
      ) : null}

      {!loading && !error && items.length > 0 ? (
        <Card variant="outlined">
          <CardContent>
            <MarketGrowthDataTable
              rowKey="id"
              rows={tableRows}
              headCells={headCells}
              actions={tableActions}
              defaultSort={{ orderBy: "joinedAt", order: "desc" }}
              tableMinWidth={900}
            />
          </CardContent>
        </Card>
      ) : null}

      <AdminActionConfirmDialog
        open={actionState.open}
        title={actionConfig?.title}
        description={actionConfig?.description}
        confirmLabel={actionConfig?.confirmLabel}
        confirmColor={actionConfig?.confirmColor}
        showNotes={actionConfig?.showNotes ?? false}
        saving={saving}
        onClose={() => !saving && setActionState({ open: false, row: null, action: null })}
        onConfirm={handleActionConfirm}
      />
    </MarketGrowthPageShell>
  );
};

export default MarketGrowthPartnerApprovalsPage;
